/**
 * Studio Types
 *
 * Types for the freemium studio mode system.
 */

import type { StepType, StoryStatus } from './database'

/**
 * Studio mode - determines which storage backend to use
 */
export type StudioMode = 'local' | 'cloud'

/**
 * Subscription tier - what plan the family is on
 */
export type SubscriptionTier = 'free' | 'premium'

/**
 * Subscription status - payment lifecycle state (only for premium tier)
 */
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'lapsed'

/**
 * Feature limits based on studio mode and subscription state
 */
export interface StudioLimits {
  maxActiveStories: number
  canAccessDashboard: boolean
  canAccessBadges: boolean
  canAccessSkillTracking: boolean
  canExportStories: boolean
  readOnly: boolean
}

// ============================================================================
// Local Storage Types
// ============================================================================

/**
 * Current version of the local storage schema
 */
export const LOCAL_STUDIO_VERSION = 1

/**
 * Step content stored locally
 */
export interface LocalStepContent {
  content: string
  wordCount: number
  updatedAt: string  // ISO string
}

/**
 * A story stored in localStorage
 */
export interface LocalActiveStory {
  id: string           // UUID generated client-side
  title: string
  coverEmoji: string
  status: StoryStatus
  wordCount: number
  createdAt: string    // ISO string
  updatedAt: string    // ISO string
  steps: Record<StepType, LocalStepContent>
}

/**
 * Migration state tracking
 */
export interface LocalMigrationState {
  status: 'none' | 'started' | 'completed' | 'failed'
  startedAt?: string
  completedAt?: string
  error?: string
  cloudStoryId?: string  // ID of the migrated story in cloud
}

/**
 * Complete local studio state - stored in localStorage
 */
export interface LocalStudioStateV1 {
  version: 1
  childId: string
  activeStory: LocalActiveStory | null
  migration?: LocalMigrationState
}

/**
 * localStorage key helpers
 */
export const LOCAL_STORAGE_KEYS = {
  getStudioKey: (childId: string) => `inkwelljr:v1:child:${childId}:localStudio`,
  getMigrationCompletedKey: (childId: string) => `inkwelljr:v1:migration:completed:${childId}`,
  UPGRADE_BANNER_DISMISSED: 'inkwelljr:v1:upgradeBanner:dismissedAt',
} as const
